'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * The homepage's hero search merging into the header on scroll.
 *
 * The hero (HomeLiveHero) owns the scroll progress; the header owns the
 * search it reveals. They are siblings under Demo7Layout, so the homepage
 * wraps both in this provider: the header registers its search wrappers
 * (desktop row and mobile row), the hero writes the reveal straight onto
 * them every frame — no re-render per scroll event — and flips `merged`
 * once, which makes the header search focusable.
 *
 * Outside the homepage there is no provider and the header search is
 * plain and always visible.
 */
interface HomeSearchMerge {
  merged: boolean;
  setMerged: (merged: boolean) => void;
  /** Ref callback for a header search wrapper. */
  register: (el: HTMLElement | null) => void;
  /** The registered wrappers that are currently mounted. */
  targets: () => HTMLElement[];
}

const HomeSearchMergeContext = createContext<HomeSearchMerge | null>(null);

export function HomeSearchMergeProvider({ children }: { children: ReactNode }) {
  const [merged, setMerged] = useState(false);
  const els = useRef(new Set<HTMLElement>());
  const register = useCallback((el: HTMLElement | null) => {
    if (el) els.current.add(el);
    for (const e of els.current) if (!e.isConnected) els.current.delete(e);
  }, []);
  const targets = useCallback(
    () => [...els.current].filter((e) => e.isConnected),
    [],
  );
  const value = useMemo(
    () => ({ merged, setMerged, register, targets }),
    [merged, register, targets],
  );
  return (
    <HomeSearchMergeContext.Provider value={value}>
      {children}
    </HomeSearchMergeContext.Provider>
  );
}

/** Null outside the homepage. */
export function useHomeSearchMerge() {
  return useContext(HomeSearchMergeContext);
}

/**
 * Props for a header search wrapper: registered with the hero, hidden until
 * the first scroll frame paints it, and inert until merged. Empty outside
 * the homepage.
 */
export function useHomeSearchTarget() {
  const merge = useHomeSearchMerge();
  if (!merge) return {};
  return {
    ref: merge.register,
    style: { opacity: 0, clipPath: 'inset(-2px 100% -2px 0)' },
    inert: !merge.merged,
    'aria-hidden': !merge.merged,
  } as const;
}
